import { SxProps, Theme } from '@mui/material';
import { designSearchFieldSx } from './designSearchFieldSx';
import { SearchableConversationSortableFields } from 'generated-graphql/graphql';

const container: SxProps<Theme> = {
  pt: '26px'
};

const toolbar: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '30px',
  mb: '24px',
  ...designSearchFieldSx
};

// SearchInput declares `sx?: SxProps`, so this cannot carry the Theme
// generic. It needs no theme values anyway.
const searchInput: SxProps = {
  width: 341
};

const countText: SxProps<Theme> = {
  lineHeight: '26px'
};

// The design draws the underline from the font's own metrics: thinner and
// lower than the browser's default, and unbroken under descenders.
const linkCell: SxProps<Theme> = {
  textDecoration: 'underline',
  textDecorationThickness: 'from-font',
  textUnderlinePosition: 'from-font',
  textDecorationSkipInk: 'none',
  fontWeight: 500,
  lineHeight: '24px'
};

const textCell: SxProps<Theme> = {
  lineHeight: '26px'
};

const sortedHeader: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mr: '10px',
  '& .MuiSvgIcon-root': {
    fontSize: '20px'
  }
};

const errorState: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  mt: '26px'
};

const emptyState: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  py: '60px'
};

const emptyStateFiltering: SxProps<Theme> = {
  ...emptyState,
  backgroundColor: 'warning.main'
};

const emptyStateIdle: SxProps<Theme> = {
  ...emptyState,
  backgroundColor: 'customGrey.light'
};

const emptyStateIcon = (isFiltering: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: isFiltering ? 'warning.light' : 'customGrey.medium',
  color: isFiltering ? 'warning.dark' : 'primary.main',
  '& .MuiSvgIcon-root': {
    fontSize: '28px'
  }
});

const emptyStateTitle = (isFiltering: boolean): SxProps<Theme> => ({
  mt: '12px',
  fontSize: '24px',
  lineHeight: '32px',
  fontWeight: 600,
  color: isFiltering ? 'warning.dark' : 'text.primary'
});

const emptyStateBody: SxProps<Theme> = {
  mt: '32px',
  fontSize: '16px',
  lineHeight: '26px',
  color: 'warning.dark'
};

const noResults: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  py: '20px',
  fontSize: '14px',
  lineHeight: '20px',
  color: 'customGrey.dark'
};

// TableV2's header colours and spacing are shared with every other table, so
// the Figma values for this tab are scoped here. The sorted selector needs one
// more attribute than TableV2's own to win.
const table = (sortField?: string): SxProps<Theme> => ({
  // The scroll fade is placed against the table's own scroll area.
  position: 'relative',
  '&:focus-visible': {
    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px'
  },
  '& .rdt_TableHeadRow, & .rdt_TableCol': {
    backgroundColor: 'customGrey.medium'
  },
  '& .rdt_TableHeadRow': {
    minHeight: '56px'
  },
  '& .rdt_TableCol': {
    borderRight: (theme) => `1px solid ${theme.palette.white}`,
    padding: '12px 16px'
  },
  '& .rdt_TableCol_Sortable': {
    width: '100%',
    justifyContent: 'space-between'
  },
  '& .rdt_TableCol_Sortable > .MuiBox-root': {
    flex: 1,
    justifyContent: 'space-between'
  },
  '& .rdt_TableCol_Sortable span': {
    whiteSpace: 'nowrap'
  },
  // "Score" needs about 44px and its 96px column leaves 43px beside the icon,
  // so the table library would cut it to "Sco...".
  '& .rdt_TableCol_Sortable > div': {
    overflow: 'visible'
  },
  // The table library sizes its sort icon at 18px with a rule this has to
  // outrank.
  '& .rdt_TableCol .__rdt_custom_sort_icon__ svg': {
    width: '20px',
    height: '20px',
    fontSize: '20px'
  },
  // The filter icon's button padding would push the icon 6px in from the
  // design's 16px edge.
  '& .rdt_TableCol .MuiIconButton-root': {
    margin: '-6px'
  },
  '& [data-table-filters]': {
    gap: '10px'
  },
  // Figma draws the 1px border inside the chip, so 3px keeps it at 28px.
  '& .MuiChip-outlined': {
    height: 'auto',
    py: '3px'
  },
  '& .MuiChip-outlined .MuiChip-deleteIcon': {
    fontSize: '16px'
  },
  // With the 10px chip gap this puts "Clear all filters" 20px after them.
  '& [data-table-filters] > .MuiLink-button': {
    ml: '10px',
    fontSize: '14px',
    lineHeight: '20px'
  },
  '& .rdt_TableRow': {
    minHeight: '96px'
  },
  '& .rdt_TableBody .rdt_TableRow': {
    borderBottom: 'none'
  },
  '& .rdt_TableCell': {
    alignItems: 'flex-start',
    padding: '16px'
  },
  '& .rdt_TableBody .rdt_TableCell:first-of-type': {
    pt: '18px'
  },
  '& .MuiTablePagination-root .MuiTablePagination-toolbar': {
    gap: '15px',
    pl: 0,
    pr: 0,
    minHeight: '52px'
  },
  '& .MuiTablePagination-root .MuiTablePagination-selectLabel, & .MuiTablePagination-root .MuiTablePagination-displayedRows':
    {
      m: 0,
      fontSize: '14px',
      lineHeight: '20px',
      color: 'customGrey.dark'
    },
  '& .MuiTablePagination-root .MuiTablePagination-input': {
    m: 0,
    mr: '25px',
    height: '52px',
    boxSizing: 'border-box',
    border: (theme) => `1px solid ${theme.palette.customGrey.dark}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: 'customGrey.dark'
  },
  '& .MuiTablePagination-root .MuiTablePagination-input .MuiTablePagination-select':
    {
      p: '10px 55px 10px 15px',
      lineHeight: '20px',
      minHeight: 'auto'
    },
  '& .MuiTablePagination-root .MuiTablePagination-selectIcon': {
    fontSize: '20px',
    right: '15px',
    color: 'customGrey.dark'
  },
  '& .MuiTablePagination-root .MuiTablePagination-actions': {
    ml: 0
  },
  '& .MuiTablePagination-root .MuiTablePagination-actions svg': {
    fontSize: '20px'
  },
  ...(sortField && {
    [`& .rdt_TableCol[data-column-id='${sortField}']`]: {
      backgroundColor: 'customGrey.dark',
      color: 'customGrey.light'
    }
  }),
  // Score's 96px column has no room for the direction arrow and the sort
  // icon both, so a sorted Score shows the direction arrow alone.
  ...(sortField ===
    SearchableConversationSortableFields.PreferredAnswersScore && {
    [`& .rdt_TableCol[data-column-id='${sortField}'] .__rdt_custom_sort_icon__`]:
      {
        display: 'none'
      }
  })
});

export const crossCampaignConversationsSx = {
  container,
  toolbar,
  table,
  searchInput,
  countText,
  linkCell,
  textCell,
  sortedHeader,
  errorState,
  emptyStateFiltering,
  emptyStateIdle,
  emptyStateIcon,
  emptyStateTitle,
  emptyStateBody,
  noResults
};
